import React from "react";
import { Pencil, Trash2, ShieldCheck, UserCog, FileText, Mail, Car, History, MapPin } from "lucide-react";
import { companyShort } from "../../companies";
import { fmtDate, seniorityShort, seniorityLabel, scattoInfo, seniorityBracket } from "./adminUtils";
import { ContractBadge } from "./ContractBadge";

const RoleCell = ({ e }) => {
  if (e.role === "admin") {
    return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#009640] bg-green-50 px-2 py-0.5 rounded-full"><ShieldCheck size={12} /> Admin HR</span>;
  }
  if (e.role === "responsabile") {
    return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#0d6cb8] bg-blue-50 px-2 py-0.5 rounded-full"><UserCog size={12} /> Responsabile</span>;
  }
  return <span className="text-[13px] text-gray-600">{e.job_title}</span>;
};

const CcnlCell = ({ e, ccnlName }) => {
  const pct = Number(e.part_time_pct ?? 100);
  return (
    <div>
      <div className="text-[13px] text-gray-700 max-w-[170px] truncate" title={ccnlName || ""}>{ccnlName || <span className="text-gray-400">—</span>}</div>
      {e.level && <span data-testid={`emp-level-badge-${e.matricola}`} className="inline-block mt-0.5 text-[10px] font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">{e.level}</span>}
      {pct < 100
        ? <span className="inline-block mt-0.5 ml-1 text-[10px] font-bold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">Part-time {pct}%</span>
        : <span className="block text-[11px] text-gray-400">Full-time</span>}
    </div>
  );
};

const OnboardingStatus = ({ e }) => {
  if (!e.welcome_sent_at) return null;
  const changed = !e.must_change_password;
  return (
    <div className="text-[10.5px] text-gray-400 mt-0.5" data-testid={`onboarding-status-${e.matricola}`}>
      Benvenuto: {fmtDate(e.welcome_sent_at)} ·{" "}
      {changed
        ? <span className="text-[#009640] font-semibold">password cambiata</span>
        : <span className="text-amber-600 font-semibold">in attesa cambio password</span>}
    </div>
  );
};

export const EmployeeRow = ({ e, onEdit, onDelete, onContract, onSeniority, onWelcome, ccnlName, ccnl, car }) => {
  const scatto = scattoInfo(e.hire_date, ccnl);
  return (
  <tr className={"border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors" + (e.active === false ? " opacity-60" : "")}>
    <td className="px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-full bg-[#16345c] text-white flex items-center justify-center text-[12px] font-bold shrink-0">{e.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}</span>
        <div>
          <div className="text-[14px] font-bold text-[#16345c] flex items-center gap-1.5 flex-wrap">
            {e.name}
            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{companyShort(e.company)}</span>
            {e.is_external && <span className="text-[9px] font-bold uppercase bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">Esterno</span>}
            {e.active === false && <span data-testid={`emp-terminated-${e.matricola}`} className="text-[9px] font-bold uppercase bg-gray-300 text-gray-700 px-1.5 py-0.5 rounded-full">Cessato</span>}
            {car && <span data-testid={`car-icon-${e.matricola}`} title={`Auto: ${car.car_model || ""}${car.car_model && car.car_plate ? " · " : ""}${car.car_plate || ""}`} className="inline-flex items-center text-[#0d6cb8]"><Car size={13} /></span>}
            <ContractBadge iso={e.contract_end} />
          </div>
          <div className="text-[11px] text-gray-500">{e.matricola} · {e.email}</div>
          <OnboardingStatus e={e} />
        </div>
      </div>
    </td>
    <td className="px-4 py-3 text-[13px] text-gray-600">
      {e.dept}
      <span data-testid={`emp-sede-${e.matricola}`} className="flex items-center gap-1 text-[11px] text-gray-400 mt-0.5" title="Sede di lavoro"><MapPin size={11} /> {e.sede || "Roma"}</span>
    </td>
    <td className="px-4 py-3"><RoleCell e={e} /></td>
    <td className="px-4 py-3"><CcnlCell e={e} ccnlName={ccnlName} /></td>
    <td className="px-4 py-3">
      <span className="text-[13px] text-gray-700" title={e.hire_date ? seniorityLabel(e.hire_date) : ""} data-testid={`seniority-${e.matricola}`}>{e.hire_date ? seniorityShort(e.hire_date) : <span className="text-gray-400">—</span>}</span>
      {e.hire_date && seniorityBracket(e.hire_date, e.contract_end) && <span data-testid={`seniority-bracket-${e.matricola}`} className="block mt-0.5 w-fit text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">{seniorityBracket(e.hire_date, e.contract_end)}</span>}
      {scatto && scatto.reachedNow && <span className="block mt-0.5 w-fit text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">{scatto.matured}° scatto!</span>}
      {scatto && !scatto.reachedNow && scatto.upcoming && <span className="block mt-0.5 w-fit text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{scatto.matured + 1}° scatto tra {scatto.monthsToNext}m</span>}
      {scatto && !scatto.reachedNow && !scatto.upcoming && scatto.matured > 0 && <span className="block mt-0.5 w-fit text-[10px] text-gray-400">{scatto.matured}/{scatto.max} scatti</span>}
    </td>
    <td className="px-4 py-3 text-right text-[13px] font-semibold text-[#16345c]">{e.ferie_balance} gg</td>
    <td className="px-4 py-3">
      {e.contract_end ? (
        <div className="flex flex-col">
          <span className="text-[13px] text-gray-700">{fmtDate(e.contract_end)}</span>
          <ContractBadge iso={e.contract_end} className="w-fit mt-0.5" />
        </div>
      ) : <span className="text-gray-400 text-[13px]">Indeterminato</span>}
    </td>
    <td className="px-4 py-3">
      <div className="flex justify-end gap-2">
        <button onClick={() => onContract(e)} data-testid={`contract-pdf-${e.matricola}`} title="Scarica scheda contratto (PDF)" className="w-8 h-8 flex items-center justify-center rounded-sm text-[#0d6cb8] hover:bg-blue-50 transition-colors"><FileText size={15} /></button>
        <button onClick={() => onSeniority(e)} data-testid={`seniority-pdf-${e.matricola}`} title="Scarica riepilogo anzianità/scatti (PDF)" className="w-8 h-8 flex items-center justify-center rounded-sm text-emerald-600 hover:bg-emerald-50 transition-colors"><History size={15} /></button>
        <button onClick={() => onWelcome(e)} data-testid={`welcome-email-${e.matricola}`} title="Invia email di benvenuto (genera password temporanea)" className="w-8 h-8 flex items-center justify-center rounded-sm text-[#009640] hover:bg-green-50 transition-colors"><Mail size={15} /></button>
        <button onClick={() => onEdit(e)} className="w-8 h-8 flex items-center justify-center rounded-sm text-[#16345c] hover:bg-[#16345c]/10 transition-colors"><Pencil size={15} /></button>
        <button onClick={() => onDelete(e)} className="w-8 h-8 flex items-center justify-center rounded-sm text-[#c00510] hover:bg-red-50 transition-colors"><Trash2 size={15} /></button>
      </div>
    </td>
  </tr>
  );
};
